import { useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import Reveal from "@/components/site/Reveal";
import { VIDEO } from "@/constants/testIds";
import { openCalendly } from "@/lib/constants";

const scrollTo = (id) => (e) => {
  e.preventDefault();
  document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
};export default function VideoSection() {
  const [open, setOpen] = useState(false);

  return (
    <section
      id="see-how-it-works"
      data-testid={VIDEO.root}
      style={{
        background: "var(--color-bg-soft)",
        paddingTop: 80,
        paddingBottom: 80,
      }}
    >
      <div
        className="container-narrow"
        style={{ maxWidth: 900, textAlign: "center" }}
      >
        <Reveal>
          <div className="eyebrow mb-5" style={{ justifyContent: "center" }}>
            See how it works
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="type-section-heading mb-4">
            Results you can trust. Hear from Dan.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p
            className="type-body mb-10"
            style={{
              maxWidth: 600,
              margin: "0 auto 40px",
              lineHeight: 1.6,
            }}
          >
            Learn how SmartEdge helps businesses unlock hidden revenue through
            compliant, results-based programs.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div
            className="relative mx-auto se-card"
            style={{
              maxWidth: 800,
              padding: 0,
              overflow: "hidden",
              borderRadius: 8,
              cursor: "pointer",
            }}
            onClick={() => setOpen(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setOpen(true);
              }
            }}
            data-testid={VIDEO.play}
            aria-label="Play founder video"
          >
            <div
              style={{
                position: "relative",
                width: "100%",
                aspectRatio: "16 / 9",
                overflow: "hidden",
              }}
            >
              <img
                src="/images/founder-dan.jpeg"
                alt="Dan Paul, Founder of SmartEdge Business Solutions"
                loading="lazy"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  transition: "transform 0.7s cubic-bezier(0.4,0,0.2,1)",
                }}
              />
              {/* Vignette overlay for text/button contrast */}
              <div
                aria-hidden="true"
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, rgba(6,27,49,0.05) 0%, rgba(6,27,49,0.35) 100%)",
                  pointerEvents: "none",
                }}
              />
              {/* Play button */}
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.96 }}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%,-50%)",
                  width: 84,
                  height: 84,
                  borderRadius: 999,
                  background: "var(--color-purple)",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow:
                    "0 20px 40px -12px rgba(83,58,253,0.55), 0 0 0 8px rgba(83,58,253,0.18)",
                }}
              >
                <Play size={30} fill="#ffffff" strokeWidth={0} style={{ marginLeft: 4 }} />
              </motion.div>
              {/* Bottom caption strip */}
              <div
                style={{
                  position: "absolute",
                  bottom: 20,
                  left: 24,
                  color: "#ffffff",
                  textShadow: "0 1px 6px rgba(0,0,0,0.4)",
                  textAlign: "left",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    fontWeight: 500,
                    opacity: 0.9,
                    marginBottom: 4,
                  }}
                >
                  Founder message
                </div>
                <div style={{ fontSize: 17, fontWeight: 400, letterSpacing: "-0.01em" }}>
                  Dan Paul  ·  SmartEdge Business Solutions
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.25}>
          <div
            className="type-caption mt-5"
            style={{ color: "var(--color-body)" }}
          >
            Dan Paul, Founder  ·  SmartEdge Business Solutions
          </div>
        </Reveal>

        <Reveal delay={0.3} className="mt-10">
          <button
            type="button"
            onClick={openCalendly}
            className="btn-ghost"
            data-testid={VIDEO.cta}
          >
            Ready to talk? Schedule a call →
          </button>
        </Reveal>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid={VIDEO.dialog}
          className="max-w-2xl"
          style={{
            background: "#ffffff",
            border: "1px solid var(--color-border)",
            borderRadius: 8,
            padding: 0,
            overflow: "hidden",
          }}
        >
          <div style={{ padding: "32px 32px 8px" }}>
            <DialogHeader>
              <DialogTitle
                style={{
                  fontSize: 22,
                  fontWeight: 300,
                  letterSpacing: "-0.22px",
                  color: "var(--color-heading)",
                  fontFamily: "var(--font-primary)",
                }}
              >
                Founder message coming soon
              </DialogTitle>
              <DialogDescription
                style={{
                  fontSize: 15,
                  color: "var(--color-body)",
                  fontFamily: "var(--font-primary)",
                  lineHeight: 1.5,
                  fontWeight: 300,
                  marginTop: 8,
                }}
              >
                Dan is finishing up the founder walkthrough — it will be live here shortly.
                In the meantime, the fastest way to get answers about your business is a
                15-minute discovery call. No obligation, no upfront costs.
              </DialogDescription>
            </DialogHeader>
          </div>
          <div
            style={{
              padding: "12px 32px 28px",
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              justifyContent: "flex-end",
              borderTop: "1px solid var(--color-border)",
              background: "var(--color-bg-soft)",
              marginTop: 20,
            }}
          >
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setOpen(false)}
              data-testid={VIDEO.dialogClose}
            >
              Not right now
            </button>
            <a
              href="#contact"
              className="btn-primary"
              onClick={(e) => {
                setOpen(false);
                openCalendly();
                e.preventDefault();
              }}
            >
              Schedule a call
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
